import React, {createContext, useContext, useMemo} from 'react';

import {useViewAction} from '../../../state/views/hooks';
import * as PanelBankSectionConfigActions from '../../../state/views/panelBankSectionConfig/actions';
import {useSectionSettings} from '../../../state/views/panelBankSectionConfig/selectors';
import {type Ref as SectionRef} from '../../../state/views/panelBankSectionConfig/types';
import {type DispatchableAction} from '../../../types/redux';
import {useWorkspaceInheritedSettings} from './hooks/useWorkspaceInheritedSettings';
import {useSectionLinePlotSettings} from './runLinePlots/utils/getLinePlotSettings';
import {
  type SectionContextInheritedSettings,
  type SectionContextMediaSettings,
} from './sectionSettingsContextTypes';
import {type Complete, type LinePlotSettings} from './types';
import {useWorkspaceSettingsContext} from './WorkspaceSettingsContextProvider';

export type SectionSettingsContextType = {
  /**
   * Set of settings that are either inherited from workspace or defined at the section level.
   * Media settings are not included; use per-field hooks to read them.
   */
  inheritedSettings: SectionContextInheritedSettings;
  /**
   * Section level settings defined in view spec
   */
  linePlot: Complete<LinePlotSettings>;
  sectionRef: SectionRef;
  updateLinePlotSectionSettings: (
    settings: Partial<LinePlotSettings>
  ) => DispatchableAction;
  /** Raw section-level media settings from view spec (migrated fields omitted from type). */
  sectionMediaSettings: SectionContextMediaSettings;
};

export const SectionSettingsContext = createContext<
  SectionSettingsContextType | undefined
>(undefined);

type SectionSettingsProps = {
  sectionRef: SectionRef;
  children: React.ReactNode;
};

/**
 * This context provider is used in conjuction with WorkspaceSettingsContextProvider.
 * We use these two context providers to handle cascading settings logic.
 */
export const SectionSettingsContextProvider = ({
  sectionRef,
  children,
}: SectionSettingsProps) => {
  const workspaceSettingsContext = useWorkspaceSettingsContext();
  const sectionSettings = useSectionSettings(sectionRef);
  const sectionLinePlotSettings = useSectionLinePlotSettings(sectionSettings);
  const sectionMediaSettings = sectionSettings?.media;

  // This hook determines if workspace or section setting should be used.
  // We want UX to reflect if section level is using workspace level setting or not.
  const inheritedSettings = useWorkspaceInheritedSettings(
    workspaceSettingsContext,
    sectionLinePlotSettings
  );

  const updateLinePlotSectionSettings = useViewAction(
    sectionRef,
    PanelBankSectionConfigActions.updateLinePlotSectionSettings
  );

  const value = useMemo(
    () => ({
      inheritedSettings,
      linePlot: sectionLinePlotSettings,
      sectionRef,
      sectionSettings,
      updateLinePlotSectionSettings,
      sectionMediaSettings: sectionMediaSettings ?? {},
    }),
    [
      inheritedSettings,
      sectionLinePlotSettings,
      sectionRef,
      sectionSettings,
      updateLinePlotSectionSettings,
      sectionMediaSettings,
    ]
  );
  return (
    <SectionSettingsContext.Provider value={value}>
      {children}
    </SectionSettingsContext.Provider>
  );
};

export const useSectionSettingsContext = (): SectionSettingsContextType => {
  const value = useContext(SectionSettingsContext);

  if (value == null) {
    throw new Error(
      'useSectionSettingsContext must be used within a SectionSettingsContextProvider'
    );
  }

  return value;
};

export const useSectionSettingsContextMaybe = () => {
  return useContext(SectionSettingsContext);
};
